/**
 * A stock count: what the shelf says, set against what the books say.
 *
 * Pick the store, then either load every item it holds (a full count) or
 * scan only the ones being checked (a spot count, or goods written off as
 * damaged). The difference is shown line by line before saving -- a
 * shortage in red, an excess in green -- and, for whoever may see costs,
 * what it is worth. Lines left blank are not counted and not changed.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Box, Button, IconButton, MenuItem, Paper, Stack, TextField, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import ListIcon from '@mui/icons-material/PlaylistAddOutlined'
import { useTranslation } from 'react-i18next'

import { createAdjustment, fetchInvItems, fetchStores } from '../api/endpoints'
import { errorMessage } from '../api/client'
import { P, type AdjustReason, type InvItem } from '../api/types'
import { useAuth } from '../store/AuthContext'
import { useUnsavedChanges } from '../store/UnsavedChanges'
import { useHotkeys } from '../hotkeys/HotkeyProvider'
import Keycap from '../components/Keycap'
import ScreenTitle from '../components/ScreenTitle'
import { sanitize } from '../vouchers/money'
import { toHijri } from '../reports/hijri'
import { today } from '../dates'
import { c, fmtMoney } from '../theme'
import { fmtQty } from './qty'
import { factorOf } from './SaleEntry'
import { ItemPicker, StoreSelect, unitsOf } from './bits'

interface Line { key: number; item: InvItem | null; unit: string; counted: string }

let nextKey = 1
const blank = (): Line => ({ key: nextKey++, item: null, unit: '', counted: '' })
const REASONS: AdjustReason[] = ['COUNT', 'DAMAGED', 'EXPIRED']

export default function CountEntry() {
  const { t, i18n } = useTranslation()
  const nav = useNavigate()
  const qc = useQueryClient()
  const { can } = useAuth()
  const seesCost = can(P.reportFinancial)

  const stores = useQuery({ queryKey: ['inv-stores'], queryFn: fetchStores })
  const items = useQuery({ queryKey: ['inv-items', '', '', 'active'],
                           queryFn: () => fetchInvItems({ active: true }) })
  const [date, setDate] = useState(today())
  const [storeId, setStoreId] = useState<number | ''>('')
  const [reason, setReason] = useState<AdjustReason>('COUNT')
  const [notes, setNotes] = useState('')
  const [lines, setLines] = useState<Line[]>([blank()])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (storeId === '' && stores.data?.length) {
      setStoreId((stores.data.find((s) => s.is_active && s.kind === 'WAREHOUSE') ?? stores.data[0]).id)
    }
  }, [stores.data, storeId])

  const book = (it: InvItem) => Number(it.stock[String(storeId)] ?? 0)
  const counted = (l: Line) => (l.counted === '' ? null
    : Number(l.counted) * factorOf(l.item!, l.unit))
  const filled = lines.filter((l) => l.item && l.counted !== '')

  // Per item, because the same item may be counted on two shelves.
  const diffs = useMemo(() => {
    const per = new Map<number, { it: InvItem; c: number }>()
    for (const l of filled) {
      const prev = per.get(l.item!.id)
      per.set(l.item!.id, { it: l.item!, c: (prev?.c ?? 0) + (counted(l) ?? 0) })
    }
    return [...per.values()].map(({ it, c: n }) => ({
      it, diff: +(n - book(it)).toFixed(3),
      value: Number(it.avg_cost ?? 0) * (n - book(it)) }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filled, storeId])
  const changed = diffs.filter((d) => d.diff !== 0)
  const net = diffs.reduce((a, d) => a + d.value, 0)

  function loadAll() {
    const held = (items.data?.items ?? []).filter((it) => book(it) !== 0)
    const have = new Set(lines.filter((l) => l.item).map((l) => l.item!.id))
    const add = held.filter((it) => !have.has(it.id)).map((it) => ({
      key: nextKey++, item: it, unit: it.base_unit, counted: '' }))
    setLines((ls) => [...ls.filter((l) => l.item), ...add, blank()])
  }

  const setLine = (key: number, patch: Partial<Line>) => setLines((ls) => {
    const next = ls.map((l) => (l.key === key ? { ...l, ...patch } : l))
    return next[next.length - 1].item ? [...next, blank()] : next
  })
  const cells = useRef<Record<string, HTMLInputElement | null>>({})
  const focusCell = (key: number, col: string) =>
    requestAnimationFrame(() => cells.current[`${key}:${col}`]?.focus())

  const save = useMutation({
    mutationFn: () => createAdjustment({
      doc_date: date, store_id: Number(storeId), reason, notes: notes.trim() || null,
      lines: filled.map((l) => ({ item_code: l.item!.code, counted: l.counted, unit: l.unit })),
    }),
    onSuccess: (x) => {
      void qc.invalidateQueries({ queryKey: ['inv-items'] })
      void qc.invalidateQueries({ queryKey: ['inv-adjustments'] })
      void qc.invalidateQueries({ queryKey: ['ledger-balances'] })
      setLines([blank()])
      nav(`/inventory/counts/${x.id}`, { replace: true, state: { saved: true } })
    },
    onError: (e) => setError(errorMessage(e)),
  })
  useUnsavedChanges(filled.length > 0 && !save.isPending && !save.isSuccess)

  const problems = [
    storeId === '' && t('inv.needStore'),
    filled.length === 0 && t('stock.needCount'),
    filled.length > 0 && changed.length === 0 && t('stock.countAgrees'),
  ].filter(Boolean) as string[]
  const ready = problems.length === 0

  useHotkeys('count-entry', [
    { combo: 'ctrl+enter', label: t('common.save'), allowInInput: true,
      handler: () => { if (ready && !save.isPending) save.mutate() } },
    { combo: 'alt+a', label: t('inv.addLine'), allowInInput: true,
      handler: () => { const b = blank(); setLines((ls) => [...ls, b]); focusCell(b.key, 'item') } },
  ])

  if (!can(P.inventoryAdjust)) return <Alert severity="warning">{t('report.noPermission')}</Alert>
  const options = items.data?.items ?? []

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ minWidth: 0 }}>
        <ScreenTitle titleKey="stock.newCount" sub={t('stock.newCountSub')} />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ px: 2.5, pt: 1, pb: 2 }}
          alignItems={{ md: 'flex-start' }}>
          <StoreSelect label={t('inv.store')} value={storeId} stores={stores.data ?? []}
            onChange={(v) => { setStoreId(v); setLines([blank()]) }} sx={{ flex: 1, minWidth: 220 }} />
          <TextField select size="small" label={t('inv.reason')} value={reason}
            sx={{ width: { md: 200 } }} onChange={(e) => setReason(e.target.value as AdjustReason)}>
            {REASONS.map((r) => <MenuItem key={r} value={r}>{t(`stock.reason${r}`)}</MenuItem>)}
          </TextField>
          <Box sx={{ width: { md: 190 } }}>
            <TextField type="date" size="small" label={t('entry.date')} value={date} fullWidth
              InputLabelProps={{ shrink: true }} inputProps={{ max: today() }}
              onChange={(e) => setDate(e.target.value)} />
            <Typography sx={{ fontSize: 11.5, color: c.inkMuted, mt: 0.4, px: 0.25 }}>
              {date ? toHijri(date, i18n.language) : ''}</Typography>
          </Box>
          <Button variant="outlined" startIcon={<ListIcon />} onClick={loadAll}
            disabled={storeId === '' || !items.data}>{t('stock.loadAll')}</Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mx: 2.5, mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Box sx={{ overflowX: 'auto', borderTop: `1px solid ${c.border}` }}>
          <table className="rpt" style={{ minWidth: 760 }}>
            <thead><tr>
              <th style={{ width: 32 }}>#</th>
              <th>{t('inv.item')}</th>
              <th className="num" style={{ width: 120 }}>{t('stock.books')}</th>
              <th style={{ width: 110 }}>{t('inv.unit')}</th>
              <th className="num" style={{ width: 110 }}>{t('stock.counted')}</th>
              <th className="num" style={{ width: 120 }}>{t('inv.difference')}</th>
              {seesCost && <th className="num" style={{ width: 110 }}>{t('inv.value')}</th>}
              <th style={{ width: 40 }} />
            </tr></thead>
            <tbody>
              {lines.map((l, idx) => {
                const n = l.item ? counted(l) : null
                const d = l.item && n !== null ? +(n - book(l.item)).toFixed(3) : null
                return (
                  <tr key={l.key}>
                    <td className="num" style={{ color: 'var(--ink-muted)' }}>{idx + 1}</td>
                    <td style={{ padding: '4px 8px' }}>
                      <ItemPicker options={options} value={l.item} storeId={storeId}
                        label={t('inv.itemOnLine', { n: idx + 1 })}
                        inputRef={(el) => { cells.current[`${l.key}:item`] = el }}
                        onPick={(it, unit) => {
                          if (!it) { setLine(l.key, { item: null, unit: '', counted: '' }); return }
                          setLine(l.key, { item: it, unit: unit ?? it.base_unit })
                          focusCell(l.key, 'counted')
                        }} />
                    </td>
                    <td className="num" style={{ color: 'var(--ink-muted)' }}>
                      {l.item ? `${fmtQty(book(l.item))} ${l.item.base_unit}` : ''}</td>
                    <td style={{ padding: '4px 8px' }}>
                      <TextField select size="small" fullWidth value={l.item ? l.unit : ''}
                        disabled={!l.item} onChange={(e) => setLine(l.key, { unit: e.target.value })}
                        inputProps={{ 'aria-label': t('inv.unitOnLine', { n: idx + 1 }) }}>
                        {unitsOf(l.item).map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                      </TextField>
                    </td>
                    <td style={{ padding: '4px 8px' }}>
                      <TextField size="small" value={l.counted} disabled={!l.item}
                        inputRef={(el) => { cells.current[`${l.key}:counted`] = el }}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter' || e.ctrlKey || e.metaKey) return
                          e.preventDefault()
                          const nextLine = lines[idx + 1]
                          if (nextLine) focusCell(nextLine.key, nextLine.item ? 'counted' : 'item')
                        }}
                        onChange={(e) => setLine(l.key, { counted: sanitize(e.target.value) })}
                        inputProps={{ inputMode: 'decimal', className: 'num', style: { textAlign: 'end' },
                          'aria-label': t('stock.countedOnLine', { n: idx + 1 }) }} />
                    </td>
                    <td className="num" style={{ fontWeight: 600,
                      color: d === null || d === 0 ? 'var(--ink-muted)' : d < 0 ? c.dangerInk : c.successInk }}>
                      {d === null ? '' : d === 0 ? '0' : `${d > 0 ? '+' : '−'}${fmtQty(Math.abs(d))}`}</td>
                    {seesCost && (
                      <td className="num" style={{ color: 'var(--ink-muted)' }}>
                        {d ? fmtMoney((Number(l.item!.avg_cost ?? 0) * d).toFixed(2)) : ''}</td>
                    )}
                    <td>
                      {l.item && (
                        <IconButton size="small" aria-label={t('inv.removeLine')}
                          onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}>
                          <DeleteIcon fontSize="small" /></IconButton>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ px: 2.5, py: 2 }}
          alignItems={{ sm: 'flex-start' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button size="small" startIcon={<AddIcon />}
              onClick={() => { const b = blank(); setLines((ls) => [...ls, b]); focusCell(b.key, 'item') }}>
              {t('inv.addLine')}</Button>
            <Keycap combo="alt+a" />
          </Stack>
          <TextField size="small" label={t('inv.notes')} value={notes} sx={{ flex: 1 }}
            onChange={(e) => setNotes(e.target.value)} inputProps={{ maxLength: 300 }} />
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
        <Stack direction="row" spacing={3} useFlexGap flexWrap="wrap" sx={{ flex: '1 1 360px' }}>
          {[[t('stock.countedLines'), String(filled.length)],
            [t('stock.short'), String(changed.filter((d) => d.diff < 0).length)],
            [t('stock.excess'), String(changed.filter((d) => d.diff > 0).length)]].map(([k, v]) => (
            <Box key={k}>
              <Typography sx={{ fontSize: 12, color: c.inkMuted }}>{k}</Typography>
              <Typography className="num" sx={{ fontSize: 16, fontWeight: 500, textAlign: 'start' }}>{v}</Typography>
            </Box>
          ))}
          {seesCost && (
            <Box>
              <Typography sx={{ fontSize: 12, color: c.inkMuted }}>{t('stock.netValue')}</Typography>
              <Typography className="num" sx={{ fontSize: 22, fontWeight: 600, textAlign: 'start',
                color: net < 0 ? c.dangerInk : undefined }}>SAR {fmtMoney(net.toFixed(2))}</Typography>
            </Box>
          )}
        </Stack>
        <Box sx={{ flex: '1 1 300px', maxWidth: 420 }}>
          <Button fullWidth variant="contained" size="large" disabled={!ready || save.isPending}
            onClick={() => save.mutate()}>
            {t('stock.saveCount')}
            <Box component="span" sx={{ ml: 1, display: { xs: 'none', sm: 'inline' } }}>
              <Keycap combo="ctrl+enter" /></Box>
          </Button>
          <Typography sx={{ fontSize: 12, mt: 1,
            color: problems.length && filled.length ? c.warningInk : c.inkMuted }}>
            {problems.length && filled.length ? problems[0] : t('stock.countPosts')}</Typography>
        </Box>
      </Paper>
    </Stack>
  )
}
